import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  ButtonBase,
  CircularProgress,
  Alert,
  Fade,
} from "@mui/material";
import {
  AdminPanelSettings,
  LocalHospital,
  MedicalServices,
  BusinessCenter,
  Work,
} from "@mui/icons-material";

// Mapa de iconos por roleKey
const roleIcons = {
  admin: AdminPanelSettings,
  pcp: LocalHospital,
  care_management: MedicalServices,
  ceo: BusinessCenter,
};

const RoleSelectorDialog = ({ open, roles = [], onSelectRole, loading: externalLoading }) => {
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [error, setError] = useState(null);
  const [internalLoading, setInternalLoading] = useState(false);

  const isLoading = externalLoading || internalLoading;

  const handleSelectRole = async (role) => {
    if (isLoading) return;

    setSelectedRoleId(role.id);
    setError(null);
    setInternalLoading(true);

    try {
      await onSelectRole(role.id);
    } catch (err) {
      setError(err?.message || "Failed to select role. Please try again.");
      setSelectedRoleId(null);
    } finally {
      setInternalLoading(false);
    }
  };

  const getIcon = (roleKey) => {
    const Icon = roleIcons[roleKey] || Work;
    return Icon;
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
          bgcolor: "rgba(255, 255, 255, 0.98)",
          backdropFilter: "blur(10px)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(12, 52, 61, 0.6)", backdropFilter: "blur(4px)" },
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", pb: 0, pt: 3 }}>
        <Typography variant="h5" fontWeight={700} color="#0C343D">
          Select Your Role
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          You have multiple roles assigned. Please select one to continue.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3, pt: 2 }}>
        {error && (
          <Fade in>
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          </Fade>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
          {roles.map((role) => {
            const Icon = getIcon(role.key);
            const isSelected = selectedRoleId === role.id;

            return (
              <ButtonBase
                key={role.id}
                onClick={() => handleSelectRole(role)}
                disabled={isLoading}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  borderRadius: 3,
                  border: "2px solid",
                  borderColor: isSelected ? "#0C343D" : "#e0e0e0",
                  bgcolor: isSelected ? "rgba(12, 52, 61, 0.05)" : "#fafafa",
                  transition: "all 0.2s ease",
                  width: "100%",
                  justifyContent: "flex-start",
                  "&:hover": {
                    borderColor: "#00bcd4",
                    bgcolor: "rgba(0, 188, 212, 0.04)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  },
                  "&.Mui-disabled": {
                    opacity: isSelected ? 1 : 0.5,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: isSelected ? "#0C343D" : "#e8f5e9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    transition: "all 0.2s ease",
                  }}
                >
                  {isSelected && isLoading ? (
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                  ) : (
                    <Icon sx={{ color: isSelected ? "#fff" : "#0C343D", fontSize: 26 }} />
                  )}
                </Box>

                <Box sx={{ textAlign: "left" }}>
                  <Typography variant="subtitle1" fontWeight={600} color="#0C343D">
                    {role.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {role.key}
                  </Typography>
                </Box>
              </ButtonBase>
            );
          })}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RoleSelectorDialog;
